"use client"

import { useState, useEffect } from "react"
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
} from "@mui/material"
import * as emailSettingsService from "../../services/email-settings.service"
import * as emailTemplateService from "../../services/email-template.service"
import useOrganizationParams from "../../hooks/useOrganizationParams"

const EmailTemplatePreview = () => {
  const [loading, setLoading] = useState(true)
  const [templates, setTemplates] = useState([])
  const [currentTemplate, setCurrentTemplate] = useState(null)
  const [selectedTab, setSelectedTab] = useState(0)
  const [settings, setSettings] = useState({
    smtpHost: "",
    smtpPort: "",
    smtpUsername: "",
    smtpPassword: "",
    fromEmail: "",
    fromName: "",
    useCustomSmtp: false,
  })
  const [testEmail, setTestEmail] = useState("")
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  })

  const organizationParams = useOrganizationParams()

  useEffect(() => {
    fetchData()
  }, [organizationParams])

  const fetchData = async () => {
    try {
      setLoading(true)
      // Fetch email settings
      const settingsResponse = await emailSettingsService.getEmailSettings(organizationParams)
      if (settingsResponse.success && settingsResponse.data) {
        setSettings(settingsResponse.data)
      }

      // Fetch email templates
      const templatesResponse = await emailTemplateService.getTemplates(organizationParams)

      if (templatesResponse.success && templatesResponse.data) {
        // Convert the templates object to an array
        const templatesArray = Object.entries(templatesResponse.data).map(([key, template]) => ({
          id: key,
          name: formatTemplateName(key),
          ...template,
        }))

        setTemplates(templatesArray)
        if (templatesArray.length > 0) {
          setCurrentTemplate(templatesArray[0])
        }
      } else {
        throw new Error(templatesResponse.error || "Failed to load templates")
      }
    } catch (error) {
      console.error("Error fetching data:", error)
      setSnackbar({
        open: true,
        message: "Failed to load email settings and templates",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const formatTemplateName = (name) => {
    if (!name) return ""
    return name
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  }

  const extractVariables = (content) => {
    if (!content) return []
    const regex = /{{([^{}]+)}}/g
    const matches = content.match(regex) || []
    return [...new Set(matches)].map((variable) => variable.replace(/{{|}}/g, ""))
  }

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue)
    setCurrentTemplate(templates[newValue])
  }

  const handleSettingsChange = (e) => {
    const { name, value, checked, type } = e.target
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleTemplateChange = (field, value) => {
    setCurrentTemplate((prev) => {
      if (!prev) return null
      return {
        ...prev,
        [field]: value,
      }
    })
  }

  const handleSaveSettings = async () => {
    try {
      const response = await emailSettingsService.updateEmailSettings(settings, organizationParams)
      if (response.success) {
        setSnackbar({
          open: true,
          message: "Email settings saved successfully",
          severity: "success",
        })
      } else {
        throw new Error(response.error || "Failed to save settings")
      }
    } catch (error) {
      console.error("Error saving settings:", error)
      setSnackbar({
        open: true,
        message: "Failed to save email settings",
        severity: "error",
      })
    }
  }

  const handleSaveTemplate = async () => {
    if (!currentTemplate) return

    try {
      const response = await emailTemplateService.updateTemplate(
        currentTemplate.id,
        {
          subject: currentTemplate.subject,
          htmlContent: currentTemplate.htmlContent,
          textContent: currentTemplate.textContent,
          description: currentTemplate.description,
        },
        organizationParams,
      )

      if (response.success) {
        setSnackbar({
          open: true,
          message: "Template saved successfully",
          severity: "success",
        })
      } else {
        throw new Error(response.error || "Failed to save template")
      }
    } catch (error) {
      console.error("Error saving template:", error)
      setSnackbar({
        open: true,
        message: "Failed to save template",
        severity: "error",
      })
    }
  }

  const handleResetTemplate = async () => {
    if (!currentTemplate) return

    try {
      const response = await emailTemplateService.resetTemplate(currentTemplate.id, organizationParams)

      if (response.success && response.data) {
        const updatedTemplate = {
          ...currentTemplate,
          subject: response.data.subject,
          htmlContent: response.data.htmlContent,
          textContent: response.data.textContent,
          description: response.data.description,
        }

        setCurrentTemplate(updatedTemplate)

        // Update the template in the templates array
        setTemplates((prevTemplates) =>
          prevTemplates.map((template) => (template.id === currentTemplate.id ? updatedTemplate : template)),
        )

        setSnackbar({
          open: true,
          message: "Template reset to default",
          severity: "success",
        })
      } else {
        throw new Error(response.error || "Failed to reset template")
      }
    } catch (error) {
      console.error("Error resetting template:", error)
      setSnackbar({
        open: true,
        message: "Failed to reset template",
        severity: "error",
      })
    }
  }

  const handleSendTestEmail = async () => {
    if (!testEmail || !currentTemplate) return

    try {
      const response = await emailTemplateService.sendTestEmail(testEmail, currentTemplate.id, organizationParams)

      if (response.success) {
        setSnackbar({
          open: true,
          message: "Test email sent successfully",
          severity: "success",
        })
      } else {
        throw new Error(response.error || "Failed to send test email")
      }
    } catch (error) {
      console.error("Error sending test email:", error)
      setSnackbar({
        open: true,
        message: "Failed to send test email",
        severity: "error",
      })
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    )
  }

  // Get all variables from the current template
  const allVariables = currentTemplate
    ? [
        ...extractVariables(currentTemplate.subject || ""),
        ...extractVariables(currentTemplate.htmlContent || ""),
        ...extractVariables(currentTemplate.textContent || ""),
      ]
    : []

  // Remove duplicates
  const uniqueVariables = [...new Set(allVariables)]

  return (
    <Box sx={{ width: "100%" }}>
      {/* SMTP Configuration Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          SMTP Configuration
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Box sx={{ flex: "1 1 45%" }}>
            <Typography variant="caption" display="block" gutterBottom>
              SMTP Host
            </Typography>
            <TextField
              fullWidth
              name="smtpHost"
              value={settings.smtpHost || ""}
              onChange={handleSettingsChange}
              variant="outlined"
              size="small"
            />
          </Box>
          <Box sx={{ flex: "1 1 45%" }}>
            <Typography variant="caption" display="block" gutterBottom>
              SMTP Port
            </Typography>
            <TextField
              fullWidth
              name="smtpPort"
              value={settings.smtpPort || ""}
              onChange={handleSettingsChange}
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Box sx={{ flex: "1 1 45%" }}>
            <Typography variant="caption" display="block" gutterBottom>
              SMTP Username
            </Typography>
            <TextField
              fullWidth
              name="smtpUsername"
              value={settings.smtpUsername || ""}
              onChange={handleSettingsChange}
              variant="outlined"
              size="small"
            />
          </Box>
          <Box sx={{ flex: "1 1 45%" }}>
            <Typography variant="caption" display="block" gutterBottom>
              SMTP Password
            </Typography>
            <TextField
              fullWidth
              type="password"
              name="smtpPassword"
              value={settings.smtpPassword || ""}
              onChange={handleSettingsChange}
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
          <Box sx={{ flex: "1 1 45%" }}>
            <Typography variant="caption" display="block" gutterBottom>
              From Email
            </Typography>
            <TextField
              fullWidth
              name="fromEmail"
              value={settings.fromEmail || ""}
              onChange={handleSettingsChange}
              variant="outlined"
              size="small"
            />
          </Box>
          <Box sx={{ flex: "1 1 45%" }}>
            <Typography variant="caption" display="block" gutterBottom>
              From Name
            </Typography>
            <TextField
              fullWidth
              name="fromName"
              value={settings.fromName || ""}
              onChange={handleSettingsChange}
              variant="outlined"
              size="small"
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FormControlLabel
            control={
              <Switch checked={settings.useCustomSmtp || false} onChange={handleSettingsChange} name="useCustomSmtp" />
            }
            label="Enable Custom SMTP Settings"
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TextField
              placeholder="Test Email Recipient"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ width: 250 }}
            />
            <Button variant="outlined" onClick={handleSendTestEmail} disabled={!testEmail}>
              Send Test Email
            </Button>
          </Box>
          <Button variant="contained" color="primary" onClick={handleSaveSettings}>
            Save Settings
          </Button>
        </Box>
      </Paper>

      {/* Email Templates Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Email Templates
        </Typography>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}
        >
          {templates.map((template, index) => (
            <Tab key={template.id} label={template.name} />
          ))}
        </Tabs>

        {currentTemplate && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" display="block" gutterBottom>
                Subject
              </Typography>
              <TextField
                fullWidth
                value={currentTemplate.subject || ""}
                onChange={(e) => handleTemplateChange("subject", e.target.value)}
                variant="outlined"
                size="small"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="caption" display="block" gutterBottom>
                Description
              </Typography>
              <TextField
                fullWidth
                value={currentTemplate.description || ""}
                onChange={(e) => handleTemplateChange("description", e.target.value)}
                variant="outlined"
                size="small"
              />
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              HTML Content
            </Typography>
            <Box sx={{ mb: 3, border: "1px solid #ddd", borderRadius: 1 }}>
              <Box sx={{ p: 1, borderBottom: "1px solid #ddd", display: "flex", gap: 1 }}>
                <select className="form-select" style={{ height: 38, width: 120 }}>
                  <option>Normal</option>
                </select>
                <IconButton size="small">
                  <span style={{ fontWeight: "bold" }}>B</span>
                </IconButton>
                <IconButton size="small">
                  <span style={{ fontStyle: "italic" }}>I</span>
                </IconButton>
                <IconButton size="small">
                  <span style={{ textDecoration: "underline" }}>U</span>
                </IconButton>
                <IconButton size="small">
                  <span style={{ textDecoration: "line-through" }}>S</span>
                </IconButton>
                <Divider orientation="vertical" flexItem />
                <IconButton size="small">
                  <span>•</span>
                </IconButton>
                <IconButton size="small">
                  <span>1.</span>
                </IconButton>
                <Divider orientation="vertical" flexItem />
                <IconButton size="small">
                  <span>A</span>
                </IconButton>
                <IconButton size="small">
                  <span>A</span>
                </IconButton>
                <Divider orientation="vertical" flexItem />
                <IconButton size="small">
                  <span>🔗</span>
                </IconButton>
                <IconButton size="small">
                  <span>📷</span>
                </IconButton>
                <IconButton size="small">
                  <span>⟲</span>
                </IconButton>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={12}
                value={currentTemplate.htmlContent || ""}
                onChange={(e) => handleTemplateChange("htmlContent", e.target.value)}
                variant="outlined"
                sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "none" } }}
              />
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Plain Text Content
            </Typography>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={currentTemplate.textContent || ""}
                onChange={(e) => handleTemplateChange("textContent", e.target.value)}
                variant="outlined"
              />
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Available Variables
            </Typography>
            <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                {uniqueVariables.length > 0
                  ? uniqueVariables.map((variable) => `{{${variable}}}`).join(", ")
                  : "No variables found in this template"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button variant="outlined" onClick={handleResetTemplate}>
                Reset to Default
              </Button>
              <Button variant="contained" color="primary" onClick={handleSaveTemplate}>
                Save Template
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default EmailTemplatePreview

